import React, { useState, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useUser } from '../contexts/UserContext';

/**
 * Self-contained authentication modal — login / register / Google /
 * email-verification / forgot-password / reset-password / force-logout.
 *
 * Extracted from NeuralGlassHomepage.js so the new homepage and any other
 * page can show the modal in-place without redirecting to /home-legacy.
 *
 * Relies on global CSS classes (auth-modal-overlay, auth-modal, glass,
 * close-modal, form-group, submit-btn, auth-message, auth-toggle, etc.)
 * which are bundled via NeuralGlassHomepage.css.
 *
 * Props:
 *   isOpen     — boolean, whether to render the modal
 *   onClose    — callback to dismiss
 *   onSuccess  — optional, called after successful login/verification
 *                (after `login` is dispatched to UserContext)
 */
const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const { login } = useUser();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin;

  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // When a Google sign-in is blocked by "already logged in elsewhere", we
  // hold onto the Google credential so that after the user clicks
  // "Force logout" we can finish the login automatically — no need to
  // pop up the Google flow again, and definitely no password prompt.
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState(null);

  // Reset all transient state whenever the modal closes, so reopening shows
  // a clean login form.
  useEffect(() => {
    if (isOpen) return;
    setShowVerification(false);
    setShowForgotPassword(false);
    setShowResetForm(false);
    setIsLogin(true);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setName('');
    setVerificationCode('');
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmNewPassword('');
    setMessage('');
    setIsSubmitting(false);
    setPendingGoogleCredential(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAuth = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setMessage('');

    try {
      if (isLogin) {
        const response = await fetch(`${API_BASE_URL}/api/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });

        const text = await response.text();
        let data;
        try { data = JSON.parse(text); }
        catch { setMessage('Invalid response from server'); return; }

        if (response.ok) {
          login(data.user, data.token);
          localStorage.setItem('token', data.token);
          localStorage.setItem('userData', JSON.stringify(data.user));
          setMessage('Login successful!');
          setEmail('');
          setPassword('');
          onSuccess?.(data.user);
          onClose?.();
        } else if (data.alreadyLoggedIn) {
          setMessage('You are already logged in on another device. Please use force logout below to continue.');
        } else if (data.requiresVerification) {
          setShowVerification(true);
          setMessage('Please verify your email first.');
        } else {
          setMessage(data.message || `Login failed (Status: ${response.status})`);
        }
      } else {
        if (password !== confirmPassword) {
          setMessage('Passwords do not match');
          return;
        }
        const response = await fetch(`${API_BASE_URL}/api/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, email, password }),
        });
        const data = await response.json();

        if (response.ok) {
          setShowVerification(true);
          setMessage('Registration successful! Please check your email for verification code.');
          setPassword('');
          setConfirmPassword('');
          setName('');
        } else if (data.message === 'User already exists') {
          setMessage('This email is already registered. Please login instead or use a different email.');
          setTimeout(() => {
            setIsLogin(true);
            setPassword('');
            setConfirmPassword('');
            setName('');
            setMessage('Switched to login. Please enter your password.');
          }, 2000);
        } else {
          setMessage(data.message || 'Registration failed');
        }
      }
    } catch (error) {
      setMessage(`Network error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setMessage('');

    try {
      if (!verificationCode || verificationCode.length !== 6) {
        setMessage('Please enter a valid 6-digit code');
        return;
      }
      const response = await fetch(`${API_BASE_URL}/api/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: verificationCode }),
      });
      const data = await response.json();

      if (response.ok) {
        login(data.user, data.token);
        localStorage.setItem('token', data.token);
        localStorage.setItem('userData', JSON.stringify(data.user));
        setMessage('Verification successful!');
        setEmail('');
        setVerificationCode('');
        onSuccess?.(data.user);
        onClose?.();
      } else {
        setMessage(data.message || 'Verification failed');
      }
    } catch (error) {
      setMessage(`Network error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForceLogout = async () => {
    try {
      if (isSubmitting) return;

      // Figure out which email to force-logout. For password sign-in we use
      // the email field. For Google sign-in the form is empty — decode the
      // Google JWT credential to pull the email out (signature isn't needed
      // here; the server will re-verify properly when we replay the sign-in).
      let logoutEmail = email;
      if (!logoutEmail && pendingGoogleCredential) {
        try {
          const payload = JSON.parse(
            atob(pendingGoogleCredential.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
          );
          logoutEmail = payload.email || '';
        } catch { /* malformed credential — fall through to error */ }
      }
      if (!logoutEmail) {
        setMessage('Please enter your email address first');
        return;
      }

      setIsSubmitting(true);
      setMessage('Forcing logout from all devices...');
      const response = await fetch(`${API_BASE_URL}/api/force-logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: logoutEmail }),
      });
      const data = await response.json();
      if (!response.ok) {
        setMessage(data.message || 'Force logout failed. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // If this was triggered from a blocked Google sign-in, finish the
      // sign-in automatically — no password prompt, no extra click.
      if (pendingGoogleCredential) {
        setMessage('Logging you in…');
        const credential = pendingGoogleCredential;
        setPendingGoogleCredential(null);
        setIsSubmitting(false);
        await signInWithGoogleCredential(credential);
        return;
      }

      // Password sign-in path: the user still needs to retype their
      // password to complete login, so prompt them to click Login again.
      setMessage('✅ Force logout successful! Please click Login button to login again.');
    } catch {
      setMessage('Network error during force logout. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resendVerificationCode = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      setMessage(response.ok ? 'New verification code sent to your email.' : (data.message || 'Failed to resend verification code'));
    } catch {
      setMessage('Network error. Please try again.');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setMessage('');
    if (!resetEmail) { setMessage('Please enter your email address'); return; }
    try {
      const response = await fetch(`${API_BASE_URL}/api/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail }),
      });
      const data = await response.json();
      if (response.ok) {
        setShowResetForm(true);
        setMessage('Reset code sent to your email. Please check your inbox.');
      } else {
        setMessage(data.message || 'Failed to send reset code');
      }
    } catch {
      setMessage('Network error. Please try again.');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage('');
    if (newPassword !== confirmNewPassword) { setMessage('Passwords do not match'); return; }
    try {
      const response = await fetch(`${API_BASE_URL}/api/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail, code: resetCode, newPassword }),
      });
      const data = await response.json();
      if (response.ok) {
        setMessage('Password reset successfully! You can now login with your new password.');
        setShowForgotPassword(false);
        setShowResetForm(false);
        setResetEmail('');
        setResetCode('');
        setNewPassword('');
        setConfirmNewPassword('');
      } else {
        setMessage(data.message || 'Failed to reset password');
      }
    } catch {
      setMessage('Network error. Please try again.');
    }
  };

  const resendResetCode = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resend-reset-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail }),
      });
      const data = await response.json();
      setMessage(response.ok ? 'New reset code sent to your email.' : (data.message || 'Failed to resend reset code'));
    } catch {
      setMessage('Network error. Please try again.');
    }
  };

  // Run a Google sign-in with a credential. The credential parameter is
  // either freshly returned by <GoogleLogin /> or replayed from
  // `pendingGoogleCredential` after a force-logout.
  const signInWithGoogleCredential = async (credential) => {
    try {
      setIsSubmitting(true);
      setMessage('');
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
      });
      const data = await response.json();

      if (response.ok) {
        setPendingGoogleCredential(null);
        login(data.user, data.token);
        localStorage.setItem('token', data.token);
        localStorage.setItem('userData', JSON.stringify(data.user));
        onSuccess?.(data.user);
        onClose?.();
      } else if (data.alreadyLoggedIn) {
        // Hold the credential so handleForceLogout can replay it and
        // complete the sign-in without re-prompting Google. The email is
        // typically encoded inside the JWT credential, but we don't decode
        // it here — the backend re-decodes it and finds the user.
        setPendingGoogleCredential(credential);
        setMessage('You are already logged in on another device. Please use force logout below to continue.');
      } else {
        setMessage(data.message || 'Google login failed');
      }
    } catch {
      setMessage('Google login error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSuccess = (credentialResponse) =>
    signInWithGoogleCredential(credentialResponse.credential);

  const renderAuthMessage = () => {
    if (!message) return null;
    const cls = (message.includes('already logged in') || message.includes('another device'))
      ? 'auth-message-error'
      : (message.includes('successful') || message.includes('✅') || message.includes('sent'))
        ? 'auth-message-success'
        : '';
    const showForceLogout =
      message.includes('already logged in') || message.includes('another device');

    return (
      <div className={`auth-message ${cls}`}>
        {message}
        {showForceLogout && (
          <div style={{
            marginTop: 15, textAlign: 'center', padding: 10,
            backgroundColor: 'rgba(255, 107, 107, 0.1)',
            borderRadius: 8, border: '1px solid rgba(255, 107, 107, 0.3)',
          }}>
            <p style={{ fontSize: '0.9rem', color: '#ff6b6b', margin: '0 0 10px 0' }}>
              This will log you out from all other devices and allow you to login here.
            </p>
            <button
              type="button"
              className="force-logout-btn"
              onClick={handleForceLogout}
              disabled={isSubmitting}
              style={{
                color: '#fff',
                backgroundColor: isSubmitting ? '#999' : '#ff6b6b',
                fontSize: '0.9rem',
                border: `1px solid ${isSubmitting ? '#999' : '#ff6b6b'}`,
                padding: '8px 16px',
                borderRadius: 6,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                opacity: isSubmitting ? 0.6 : 1,
              }}
            >
              {isSubmitting ? 'Processing...' : '🔓 Force Logout & Login Here'}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="auth-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="auth-modal glass">
        <button className="close-modal" onClick={onClose}>×</button>

        {showVerification ? (
          <>
            <h2>Verify Your Email</h2>
            <p>Enter the 6-digit code sent to your email</p>
            <form onSubmit={handleVerifyCode}>
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  required
                  maxLength="6"
                />
              </div>
              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
                style={{ opacity: isSubmitting ? 0.6 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
              >
                {isSubmitting ? 'Verifying...' : 'Verify & Login'}
              </button>
            </form>
            <p className="auth-switch">
              Didn't receive the code?{' '}
              <button type="button" className="auth-toggle" onClick={resendVerificationCode}>
                Resend Code
              </button>
            </p>
            {renderAuthMessage()}
          </>
        ) : showForgotPassword ? (
          <>
            <h2>{showResetForm ? 'Reset Password' : 'Forgot Password'}</h2>
            {!showResetForm ? (
              <>
                <p>Enter your email to receive a password reset code</p>
                <form onSubmit={handleForgotPassword}>
                  <div className="form-group">
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={isSubmitting}
                    style={{ opacity: isSubmitting ? 0.6 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Reset Code'}
                  </button>
                </form>
              </>
            ) : (
              <>
                <p>Enter the reset code sent to {resetEmail} and your new password</p>
                <form onSubmit={handleResetPassword}>
                  <div className="form-group">
                    <input
                      type="text"
                      placeholder="Reset Code (6 digits)"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value)}
                      required
                      maxLength="6"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="password"
                      placeholder="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength="6"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="password"
                      placeholder="Confirm New Password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      required
                      minLength="6"
                    />
                  </div>
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={isSubmitting}
                    style={{ opacity: isSubmitting ? 0.6 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                  >
                    {isSubmitting ? 'Resetting...' : 'Reset Password'}
                  </button>
                </form>
                <p className="auth-switch">
                  Didn't receive the code?{' '}
                  <button type="button" className="auth-toggle" onClick={resendResetCode} disabled={isSubmitting}>
                    Resend Code
                  </button>
                </p>
              </>
            )}
            {renderAuthMessage()}
            <p className="auth-switch">
              Remember your password?{' '}
              <button
                type="button"
                className="auth-toggle"
                onClick={() => {
                  setShowForgotPassword(false);
                  setShowResetForm(false);
                  setResetEmail('');
                  setResetCode('');
                  setNewPassword('');
                  setConfirmNewPassword('');
                  setMessage('');
                }}
              >
                Back to Login
              </button>
            </p>
          </>
        ) : (
          <>
            <h2>{isLogin ? 'Login' : 'Create Account'}</h2>

            <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0 8px' }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setMessage('Google login failed. Please try again.')}
                theme="filled_black"
                shape="rectangular"
                width="320"
                text={isLogin ? 'signin_with' : 'signup_with'}
              />
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              margin: '16px 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem',
            }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.15)' }} />
              or continue with email
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.15)' }} />
            </div>

            <form onSubmit={handleAuth}>
              {!isLogin && (
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
              )}
              <div className="form-group">
                <input
                  type="email"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {!isLogin && (
                <div className="form-group">
                  <input
                    type="password"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
              )}

              {isLogin && (
                <div className="forgot-password-link">
                  <button type="button" className="auth-toggle" onClick={() => setShowForgotPassword(true)}>
                    Forgot Password?
                  </button>
                </div>
              )}

              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
                style={{ opacity: isSubmitting ? 0.6 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
              >
                {isSubmitting ? 'Processing...' : (isLogin ? 'Login' : 'Register')}
              </button>
            </form>

            {renderAuthMessage()}

            <p className="auth-switch">
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <button type="button" className="auth-toggle" onClick={() => setIsLogin(!isLogin)}>
                {isLogin ? 'Register' : 'Login'}
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
